import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Language, CMBLLangCode, Languages } from '../../core/languages';
import { BehaviorSubject } from 'rxjs';
import { UserProfile } from '../user-profile';
import { LOCAL_STORAGE_KEYS } from '../../core/constants';
import { filter, skip } from 'rxjs/operators';
import { UserService } from '../../user-dashboard/shared/user.service';
import { AuthenticationService } from './authentication.service';

@Injectable({providedIn: 'root'})
export class LanguageService {

  public currentLanguage: BehaviorSubject<Language>;

  constructor(
    private translate: TranslateService,
    private userService: UserService,
    private authService: AuthenticationService,
  ) {}

  public initTranslation() {
    this.translate.addLangs(Languages.getNgxTranslateLocales());
    this.translate.setDefaultLang(Languages.getDefaultLanguage().ngxTranslateLocale);

    const currentLocale = localStorage.getItem(LOCAL_STORAGE_KEYS.LOCALE);
    const currentLang = Languages.resolveFromLocaleOrFallbackOnDefault(currentLocale)
    this.currentLanguage = new BehaviorSubject<Language>(currentLang);

    this.translate.use(this.currentLanguage.value.ngxTranslateLocale);

    // only update localStorage locale if it is manually set, not initialized
    this.currentLanguage.pipe(skip(1)).subscribe(lang => {
      this.translate.use(lang.ngxTranslateLocale);
      localStorage.setItem(LOCAL_STORAGE_KEYS.LOCALE, lang.cmblLangCode);

      if (this.authService.isAuthenticated()) {
        this.userService.updateLanguage(lang.cmblLangCode).subscribe(() => {});
      }
    });

    this.authService.userProfileBehavior()
      .pipe(
        skip(1),
        filter(up => !!up),
      )
      .subscribe(userProfile => {
        this.usePreferredLanguage(userProfile);
      });
  }

  public useLanguage(language: Language) {
    this.currentLanguage.next(language);
  }

  public usePreferredLanguage(currentUserProfile: UserProfile): void {
    const hasPersonalLanguage = currentUserProfile && currentUserProfile.langCode;
    const hasCarrierPreferredLanguage = currentUserProfile && currentUserProfile.carrier && currentUserProfile.carrier.preferredLocale;

    let languageToUse: CMBLLangCode;
    if (hasPersonalLanguage && Languages.isKnownLocaleLang(currentUserProfile.langCode)) {
      languageToUse = currentUserProfile.langCode;
    } else if (hasCarrierPreferredLanguage
        && localStorage.getItem(LOCAL_STORAGE_KEYS.LOCALE) == null
        && Languages.isKnownLocaleLang(currentUserProfile.carrier.preferredLocale)) {
      languageToUse = currentUserProfile.carrier.preferredLocale;
    }

    if (languageToUse) {
      this.currentLanguage.next(Languages.fromLocale(languageToUse));
    }
  }
}
